import { useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { usePrefersReducedMotion } from "@signet/ui";

/* ──────────────────────────────────────────────────────────────────────────
   Dragging rows into order (Jay, 2026-10-08: "really smooth"). Written for
   to-dos; the onboarding tracker's lines use it too (2026-11-04), so each
   caller names its own classes with `prefix` and what a press on a row's
   controls should never start (`ignore`).

   Pointer events moving the rows with transforms, not the browser's own
   drag and drop, whose ghost image cannot be animated: the row lifts and
   follows the pointer, the others slide out of its way, and on release it
   settles into the gap before the list is reordered underneath it, so
   nothing jumps. A press that moves less than a few pixels is still a click
   (which opens the to-do). Rows only move among rows of the same group, so
   a pinned to-do stays among the pinned. Alt with the up or down arrow moves
   one with the keyboard.
   ────────────────────────────────────────────────────────────────────────── */

export type Place = { before: string } | { after: string };

const THRESHOLD = 5;
const SETTLE_MS = 170;
const EDGE = 48;
const IGNORE = "button, a, input, select, textarea, [role='checkbox'], [contenteditable='true']";

type Active = {
  id: string;
  el: HTMLElement;
  rows: HTMLElement[];
  index: number;
  lo: number;
  hi: number;
  tops: number[];
  heights: number[];
  startY: number;
  startScroll: number;
  scroller: HTMLElement | null;
  pointerY: number;
  target: number;
  frame: number;
};

function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

const scrollTopOf = (scroller: HTMLElement | null) => (scroller ? scroller.scrollTop : window.scrollY);

export function useSortable({
  ids,
  groupOf,
  onMove,
  disabled = false,
  prefix,
  ignore = IGNORE,
}: {
  ids: string[];
  groupOf: (id: string) => string;
  onMove: (id: string, place: Place, order: string[]) => void;
  disabled?: boolean;
  /** Classes are `<prefix>-sorting`, `-dragging`, `-dropping`, `-settling`, and `-drag-cursor` on the body. */
  prefix: string;
  /** A press on these never starts a drag. */
  ignore?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const active = useRef<Active | null>(null);
  const pending = useRef<{ id: string; el: HTMLElement; x: number; y: number; pointerId: number } | null>(null);
  const swallowClick = useRef(false);
  const latest = useRef({ ids, groupOf, onMove });
  latest.current = { ids, groupOf, onMove };

  const range = (index: number) => {
    const { ids: list, groupOf: group } = latest.current;
    const g = group(list[index]!);
    let lo = index;
    let hi = index;
    while (lo > 0 && group(list[lo - 1]!) === g) lo--;
    while (hi < list.length - 1 && group(list[hi + 1]!) === g) hi++;
    return { lo, hi };
  };

  const layout = useCallback((a: Active) => {
    const scrolled = scrollTopOf(a.scroller) - a.startScroll;
    const dy = a.pointerY - a.startY + scrolled;
    a.el.style.transform = `translate3d(0, ${dy}px, 0) scale(1.012)`;
    const centre = a.tops[a.index]! + a.heights[a.index]! / 2 + dy;
    let target = a.index;
    for (let j = a.index + 1; j <= a.hi; j++) if (centre > a.tops[j]! + a.heights[j]! / 2) target = j;
    for (let j = a.index - 1; j >= a.lo; j--) if (centre < a.tops[j]! + a.heights[j]! / 2) target = j;
    a.target = target;
    const lift = a.heights[a.index]!;
    a.rows.forEach((row, j) => {
      if (j === a.index) return;
      const shift = a.index < j && j <= target ? -lift : target <= j && j < a.index ? lift : 0;
      row.style.transform = shift ? `translate3d(0, ${shift}px, 0)` : "";
    });
  }, []);

  // Scrolls the list (or the page) while the row is held near an edge.
  const tick = useCallback(() => {
    const a = active.current;
    if (!a) return;
    const box = a.scroller ? a.scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const over = a.pointerY - (box.bottom - EDGE);
    const under = box.top + EDGE - a.pointerY;
    const speed = over > 0 ? Math.min(18, over / 3) : under > 0 ? -Math.min(18, under / 3) : 0;
    if (speed) {
      if (a.scroller) a.scroller.scrollTop += speed;
      else window.scrollBy(0, speed);
      layout(a);
    }
    a.frame = requestAnimationFrame(tick);
  }, [layout]);

  const begin = (p: NonNullable<typeof pending.current>, y: number) => {
    const list = p.el.parentElement;
    if (!list) return;
    const rows = [...list.querySelectorAll<HTMLElement>(":scope > [data-sort-id]")];
    const index = rows.indexOf(p.el);
    if (index < 0) return;
    const { lo, hi } = range(index);
    const rects = rows.map((r) => r.getBoundingClientRect());
    const scroller = scrollerOf(p.el);
    const a: Active = {
      id: p.id, el: p.el, rows, index, lo, hi,
      tops: rects.map((r) => r.top), heights: rects.map((r) => r.height),
      startY: p.y, startScroll: scrollTopOf(scroller), scroller, pointerY: y, target: index, frame: 0,
    };
    active.current = a;
    list.classList.add(`${prefix}-sorting`);
    p.el.classList.add(`${prefix}-dragging`);
    document.body.classList.add(`${prefix}-drag-cursor`);
    window.getSelection()?.removeAllRanges();
    layout(a);
    a.frame = requestAnimationFrame(tick);
  };

  const finish = (commit: boolean) => {
    const a = active.current;
    active.current = null;
    if (!a) return;
    cancelAnimationFrame(a.frame);
    document.body.classList.remove(`${prefix}-drag-cursor`);
    const list = a.el.parentElement;
    const moved = commit && a.target !== a.index;
    // Where the row ends up: past the heights of the rows it jumped over.
    let slot = 0;
    if (moved) {
      if (a.target > a.index) for (let j = a.index + 1; j <= a.target; j++) slot += a.heights[j]!;
      else for (let j = a.target; j < a.index; j++) slot -= a.heights[j]!;
    }
    a.el.classList.add(`${prefix}-dropping`);
    a.el.style.transform = `translate3d(0, ${slot}px, 0)`;
    if (!moved) a.rows.forEach((row) => row !== a.el && (row.style.transform = ""));

    const settle = () => {
      list?.classList.add(`${prefix}-settling`);
      if (moved) {
        const order = [...latest.current.ids];
        const [id] = order.splice(a.index, 1);
        order.splice(a.target, 0, id!);
        const anchor = latest.current.ids[a.target]!;
        // Reorder the list in this same frame, then drop the transforms: the rows are already where they belong.
        flushSync(() => latest.current.onMove(a.id, a.target > a.index ? { after: anchor } : { before: anchor }, order));
      }
      a.rows.forEach((row) => (row.style.transform = ""));
      a.el.classList.remove(`${prefix}-dragging`, `${prefix}-dropping`);
      list?.classList.remove(`${prefix}-sorting`);
      requestAnimationFrame(() => list?.classList.remove(`${prefix}-settling`));
    };
    if (reduced) settle();
    else window.setTimeout(settle, SETTLE_MS);
  };

  useEffect(() => {
    const onMovePointer = (event: PointerEvent) => {
      const p = pending.current;
      if (active.current) {
        event.preventDefault();
        active.current.pointerY = event.clientY;
        layout(active.current);
        return;
      }
      if (!p || event.pointerId !== p.pointerId) return;
      if (Math.hypot(event.clientX - p.x, event.clientY - p.y) < THRESHOLD) return;
      begin(p, event.clientY);
    };
    const onUp = (event: PointerEvent) => {
      if (pending.current && event.pointerId !== pending.current.pointerId) return;
      pending.current = null;
      if (active.current) {
        swallowClick.current = true;
        // A click only follows when the press ended over the same row; never leave the flag set.
        window.setTimeout(() => (swallowClick.current = false), 0);
        finish(event.type === "pointerup");
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && active.current) finish(false);
    };
    window.addEventListener("pointermove", onMovePointer, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMovePointer);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("keydown", onKey);
    };
    // begin and finish read everything through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  /** Spread on each row. */
  const rowProps = (id: string) => ({
    "data-sort-id": id,
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      // Mouse and pen only: on a phone a drag is a scroll.
      if (disabled || event.button !== 0 || event.pointerType === "touch") return;
      if ((event.target as HTMLElement).closest(ignore)) return;
      pending.current = { id, el: event.currentTarget, x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (disabled || !event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
      const { ids: list } = latest.current;
      const index = list.indexOf(id);
      const { lo, hi } = range(index);
      const to = event.key === "ArrowUp" ? index - 1 : index + 1;
      if (to < lo || to > hi) return;
      event.preventDefault();
      const order = [...list];
      order.splice(index, 1);
      order.splice(to, 0, id);
      latest.current.onMove(id, to > index ? { after: list[to]! } : { before: list[to]! }, order);
    },
  });

  /** True once, for the click that ends a drag, so it does not also open the row. */
  const consumeClick = () => {
    if (!swallowClick.current) return false;
    swallowClick.current = false;
    return true;
  };

  return { rowProps, consumeClick };
}
